import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight, Check, Globe, Users, Bell, Shield, Eye } from 'lucide-react'

export const Route = createFileRoute('/_site/guides/status-pages')({
  component: StatusPagesGuidePage,
  head: () => ({
    meta: [
      { title: 'Public Status Pages for Uptime Monitoring — Uptrack' },
      { name: 'description', content: 'Build trust with customers using a public status page. Show real-time uptime, incident history, and maintenance schedules. Included on every Uptrack plan.' },
      { property: 'og:title', content: 'Public Status Pages — Uptrack' },
      { property: 'og:description', content: 'Show your customers what\'s up and what\'s down. Public status pages are included on every plan.' },
      { property: 'og:image', content: 'https://uptrack.app/og/guides-status-pages.png' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/guides/status-pages' },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'Why Your SaaS Needs a Public Status Page',
          description: 'How public status pages reduce support tickets, build trust, and keep customers informed during incidents.',
          datePublished: '2026-05-22',
          dateModified: '2026-05-22',
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: { '@type': 'Organization', name: 'Uptrack', url: 'https://uptrack.app', logo: { '@type': 'ImageObject', url: 'https://uptrack.app/uptrack-logo-header.png' } },
        }),
      },
    ],
  }),
})

const competitors = [
  { name: 'Statuspage (Atlassian)', price: '$79/mo', free: 'No', custom: 'Yes', included: 'Separate product' },
  { name: 'Better Stack', price: '$20/mo', free: 'Yes (1)', custom: 'Paid', included: 'Bundled' },
  { name: 'UptimeRobot', price: '$7/mo', free: 'No', custom: 'Paid', included: 'Add-on' },
  { name: 'Instatus', price: '$20/mo', free: 'Yes (1)', custom: 'Yes', included: 'Standalone' },
  { name: 'Uptrack', price: 'Free, or $3/mo', free: 'Yes (1)', custom: 'Team+', included: 'Built-in', highlight: true },
]

function StatusPagesGuidePage() {
  return (
    <>

      <>
        <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
          <h1 className="font-sans mb-4 text-4xl font-semibold">
            Why your SaaS needs a public status page
          </h1>
          <p className="mb-8 text-lg leading-[1.7] text-muted-foreground">
            When your service goes down, your inbox floods. Customers don't know if you're aware, if you're fixing it, or if they should switch to a competitor. A public status page answers all three questions before they're asked.
          </p>

          {/* The problem */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">The support ticket spiral</h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Without a status page, every outage triggers the same pattern:
            </p>
            <div className="space-y-3">
              {[
                'Customer notices something broken → opens a support ticket',
                '10 more customers notice → 10 more tickets',
                'Your team spends 30 minutes replying "we\'re aware and working on it" instead of fixing the problem',
                'Customers who didn\'t check support assume you don\'t know',
                'Trust erodes. Some start evaluating alternatives',
              ].map((step, i) => (
                <div key={i} className="flex gap-3 text-sm">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-status-down/20 text-xs text-status-down">{i + 1}</span>
                  <span className="text-muted-foreground">{step}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-lg leading-[1.7] text-muted-foreground">
              A status page breaks this cycle. Customers check the page, see you're on it, and move on. Your team fixes instead of replying.
            </p>
          </section>

          {/* What a good status page shows */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">What a good status page shows</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-border bg-card p-5">
                <Eye className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">Real-time status</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Each service shows operational, degraded, or down — updated automatically from your monitors.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <Shield className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">Uptime history</h3>
                <p className="text-lg leading-[1.7] text-body">
                  90-day uptime bars per service. Customers see your track record, not just current state.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <Bell className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">Incident updates</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Post updates during incidents. "Investigating" → "Identified" → "Resolved". Customers follow along.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <Users className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">Subscriber notifications</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Let customers subscribe via email. They get notified when incidents start and resolve — no manual broadcasting.
                </p>
              </div>
            </div>
          </section>

          {/* Use cases */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Who uses status pages</h2>
            <div className="space-y-4">
              {[
                { title: 'SaaS products', description: 'Show API uptime to customers and investors. Link from your docs footer or help center.' },
                { title: 'Agencies', description: 'Give each client their own status page. Show them you\'re monitoring their sites proactively.' },
                { title: 'Internal teams', description: 'Password-protected status page for internal services. DevOps sees everything, stakeholders see summaries.' },
                { title: 'E-commerce', description: 'Checkout down during a sale costs money. A status page lets customers know you\'re fixing it instead of abandoning carts.' },
                { title: 'Open source projects', description: 'Show your community that infrastructure is healthy. Build trust with contributors and sponsors.' },
              ].map((uc) => (
                <div key={uc.title} className="flex gap-3">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
                  <div>
                    <span className="font-semibold">{uc.title}</span>
                    <span className="text-muted-foreground"> — {uc.description}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Uptrack status pages */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Uptrack status pages</h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              Every Uptrack plan includes status pages — no separate product, no add-on fee. Assign monitors to a page, pick a slug, and share the URL.
            </p>

            <div className="rounded-lg border border-border bg-card p-5">
              <h3 className="font-sans mb-3 font-semibold">What's included by plan</h3>
              <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Status page features by plan">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="pb-2 pr-4 text-left font-medium text-muted-foreground">Feature</th>
                      <th className="pb-2 px-3 text-center font-medium text-muted-foreground">Pro</th>
                      <th className="pb-2 px-3 text-center font-medium text-muted-foreground">Team</th>
                      <th className="pb-2 pl-3 text-center font-medium text-muted-foreground">Business</th>
                    </tr>
                  </thead>
                  <tbody className="text-lg leading-[1.7] text-muted-foreground">
                    <tr className="border-b"><td className="py-2 pr-4">Status pages</td><td className="py-2 px-3 text-center">5</td><td className="py-2 px-3 text-center">Unlimited</td><td className="py-2 pl-3 text-center">Unlimited</td></tr>
                    <tr className="border-b"><td className="py-2 pr-4">90-day uptime bars</td><td className="py-2 px-3 text-center">Yes</td><td className="py-2 px-3 text-center">Yes</td><td className="py-2 pl-3 text-center">Yes</td></tr>
                    <tr className="border-b"><td className="py-2 pr-4">Subscriber notifications</td><td className="py-2 px-3 text-center">Yes</td><td className="py-2 px-3 text-center">Yes</td><td className="py-2 pl-3 text-center">Yes</td></tr>
                    <tr className="border-b"><td className="py-2 pr-4">Custom domain</td><td className="py-2 px-3 text-center">—</td><td className="py-2 px-3 text-center">Yes</td><td className="py-2 pl-3 text-center">Yes</td></tr>
                    <tr className="border-b"><td className="py-2 pr-4">Theming</td><td className="py-2 px-3 text-center">—</td><td className="py-2 px-3 text-center">Yes</td><td className="py-2 pl-3 text-center">Yes</td></tr>
                    <tr className="border-b"><td className="py-2 pr-4">Password protection</td><td className="py-2 px-3 text-center">—</td><td className="py-2 px-3 text-center">Yes</td><td className="py-2 pl-3 text-center">Yes</td></tr>
                    <tr><td className="py-2 pr-4">Whitelabel (remove branding)</td><td className="py-2 px-3 text-center">—</td><td className="py-2 px-3 text-center">—</td><td className="py-2 pl-3 text-center">Yes</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* How to set up */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Set up in 2 minutes</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">1</div>
                <div>
                  <h3 className="font-sans mb-1 font-semibold">Create a status page</h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Go to Dashboard → Status Pages → Create. Pick a name and slug (e.g., <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">status.yourcompany.com</code>).
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">2</div>
                <div>
                  <h3 className="font-sans mb-1 font-semibold">Assign monitors</h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Pick which monitors appear on the page. Each shows its current status and 90-day uptime history.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">3</div>
                <div>
                  <h3 className="font-sans mb-1 font-semibold">Share the link</h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Your page is live at <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">uptrack.app/status/your-slug</code>. Add it to your footer, docs, or help center.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-border bg-card p-4">
              <p className="mb-2 text-xs font-medium text-muted-foreground">Or create via API:</p>
              <pre tabIndex={0} className="overflow-x-auto text-sm leading-relaxed"><code className="text-muted-foreground">{`curl -X POST https://api.uptrack.app/api/status-pages \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Acme Status", "slug": "acme"}'`}</code></pre>
            </div>
          </section>

          {/* Competitor comparison */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Why not use Statuspage by Atlassian?</h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              Atlassian Statuspage is the market leader, but it's a separate product at $79/mo. Most teams don't need a standalone status page tool — they need it bundled with their monitoring.
            </p>
            <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Status page tools compared">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Provider</th>
                    <th className="pb-3 px-3 text-center font-medium text-muted-foreground">Price</th>
                    <th className="pb-3 px-3 text-center font-medium text-muted-foreground">Free tier</th>
                    <th className="pb-3 px-3 text-center font-medium text-muted-foreground">Custom domain</th>
                    <th className="pb-3 pl-3 text-center font-medium text-muted-foreground">Bundled with monitoring</th>
                  </tr>
                </thead>
                <tbody>
                  {competitors.map((c) => (
                    <tr key={c.name} className={`border-b last:border-0 ${c.highlight ? 'bg-muted' : ''}`}>
                      <td className={`py-3 pr-4 font-medium ${c.highlight ? 'text-primary' : ''}`}>{c.name}</td>
                      <td className="py-3 px-3 text-center text-muted-foreground">{c.price}</td>
                      <td className="py-3 px-3 text-center text-muted-foreground">{c.free}</td>
                      <td className="py-3 px-3 text-center text-muted-foreground">{c.custom}</td>
                      <td className={`py-3 pl-3 text-center ${c.highlight ? 'font-medium text-primary' : 'text-muted-foreground'}`}>{c.included}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">FAQ</h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-sans mb-1 font-semibold">Does the status page update automatically?</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Yes. When a monitor detects a confirmed outage (consecutive failed checks), the status page updates automatically. You can also post manual incident updates.
                </p>
              </div>
              <div>
                <h3 className="font-sans mb-1 font-semibold">Can I use my own domain?</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Yes, on Team and Business plans. Point a CNAME to Uptrack and your status page lives at <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">status.yourcompany.com</code>.
                </p>
              </div>
              <div>
                <h3 className="font-sans mb-1 font-semibold">Can I hide it from search engines?</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Yes. Team+ plans include a noindex option so your status page doesn't appear in Google results.
                </p>
              </div>
              <div>
                <h3 className="font-sans mb-1 font-semibold">How many subscribers can I have?</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Pro: 1,000, Team: 5,000, Business: 10,000. Need more? Add extra subscribers at $0.01/subscriber/month.
                </p>
              </div>
              <div>
                <h3 className="font-sans mb-1 font-semibold">Can I see an example?</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Yes — Uptrack's own status page is public at{' '}
                  <a href="https://uptrack.app/status/uptrack" className="text-primary hover:underline">uptrack.app/status/uptrack</a>.
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <div className="rounded-lg border border-border bg-muted p-8 text-center">
            <Globe className="mx-auto mb-4 h-8 w-8 text-primary" />
            <h2 className="font-sans mb-3 text-xl font-semibold">Create your status page in 2 minutes</h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              Status pages are included on every plan: 5 on Pro, unlimited on Team. No separate product, no add-on.
            </p>
            <Button size="lg" asChild>
              <Link to="/pricing">
                See plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </article>
      </>

      </>
  )
}
